import styled, { css } from "styled-components";
import { darken, lighten } from "polished";

import colors from "../colors";

const defaults = {
  baseHeight: "40px",
  baseWidth: "100%",
  font: "12px/38px 'Source Sans Pro', Helvetica, Arial, Geneva, sans-serif"
};

const setThemeColor = theme => {
  const themeColor = theme || colors.primaryTouched;

  return css`
    background-color: ${themeColor};
    border-color: ${themeColor};
  `;
};

const setDarkenAttrs = (opacity, theme) => {
  const themeColor = theme || colors.primaryTouched;
  const darkenFunc = darken(opacity, themeColor);

  return css`
    background-color: ${darkenFunc};
    border-color: ${darkenFunc};
  `;
};

const BaseButton = styled.button`
  background: none;
  background-color: ${colors.primaryTouched};
  border: 1px solid;
  border-color: ${colors.primaryTouched};
  border-radius: 0;
  box-sizing: border-box;
  color: #ffffff;
  cursor: pointer;
  display: inline-block;
  font: ${defaults.font};
  font-weight: bold;
  height: ${props => (props.height === "low" ? "30px" : defaults.baseHeight)};
  line-height: ${props => (props.height === "low" ? "28px" : "38px")};

  @include user-select(none);
  @media screen and (max-width: 660px) {
    height: 45px;
    font-size: 12px;
    line-height: ${props => (props.height === "low" ? "28px" : "43px")};
  }

  padding: 0 20px;
  position: relative;
  text-align: center;
  text-decoration: none !important;
  text-transform: uppercase;
  transition: background 200ms, border 200ms, color 200ms;
  white-space: nowrap;
  width: ${props => (props.minWidth ? "auto" : defaults.baseWidth)};

  &:hover {
    ${props => props.theme && setDarkenAttrs(0.05, colors[props.theme])};
  }

  &:active {
    ${props => props.theme && setDarkenAttrs(0.1, colors[props.theme])};
  }

  &:disabled {
    &:hover,
    &:active {
      background-color: ${colors.foxGrey};
      border-color: ${colors.foxGrey};
    }
    background-color: ${colors.foxGrey};
    border-color: ${colors.foxGrey};
    color: #ffffff;
    cursor: default;
  }
`;

const Primary = styled(BaseButton)`
  ${setThemeColor(colors.primary)};
`;

const White = styled(BaseButton)`
  background-color: ${"white"};
  border-color: ${colors.foxGrey};
  color: ${colors.primary};

  &:hover {
    background-color: ${"white"};
    border-color: ${darken(0.3, colors.foxGrey)};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.03);
    border-color: ${darken(0.3, colors.foxGrey)};
  }
`;

const ErrorRed = styled(BaseButton)`
  ${setThemeColor(colors.errorRed)};

  &:hover {
    ${props => props.theme && setDarkenAttrs(0.1, colors.errorRed)};
  }

  &:active {
    ${props => props.theme && setDarkenAttrs(0.15, colors.errorRed)};
  }
`;

const Hollow = styled(BaseButton)`
  background: none;
  border-color: ${colors.foxGrey};
  color: ${colors.primaryTouched};

  &:hover {
    background: none;
    border-color: ${darken(0.3, colors.foxGrey)};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.03);
    border-color: ${darken(0.3, colors.foxGrey)};
  }

  &:disabled {
    &:hover {
      background: none;
      border-color: ${colors.foxGrey};
    }
    background: none;
    border-color: ${colors.foxGrey};
    color: ${colors.goatGrey};
    cursor: default;
  }
`;

const HollowDark = styled(BaseButton)`
  background: none;
  border-color: #ffffff;
  color: #ffffff;

  &:hover {
    background-color: #ffffff;
    border-color: ${lighten(0.3, colors.wolfGrey)};
    color: ${colors.pureBlack} !important;
  }

  &:active {
    background-color: #ffffff;
    border-color: ${colors.wolfGrey};
    color: ${colors.pureBlack};
  }

  &:disabled {
    &:active {
    }
    &:hover {
    }
    background: none;
    border-color: ${colors.foxGrey};
    color: ${colors.goatGrey};
    cursor: default;
  }
`;

const Apple = styled(BaseButton)`
  && {
    align-items: center;
    background: ${colors.pureWhite};
    border: 1px solid ${colors.pureBlack};
    color: ${colors.pureBlack};
    display: flex;
    font-size: 14px;
    font-weight: 400;
    justify-content: center;
    text-transform: none;

    &:hover {
      background: ${colors.pureWhite};
      border: 1px solid ${colors.pureBlack};
      color: ${colors.pureBlack};
    }

    @media screen and (max-width: 660px) {
      font-size: 14px;
    }
  }
`;

const Google = styled(BaseButton)`
  && {
    align-items: center;
    background: ${colors.pureWhite};
    border: 1px solid #949494;
    color: ${colors.pureBlack};
    display: flex;
    font-size: 14px;
    font-weight: 400;
    justify-content: center;
    text-transform: none;

    &:hover {
      background: ${colors.pureWhite};
      border: 1px solid #949494;
      color: ${colors.pureBlack};
    }

    @media screen and (max-width: 660px) {
      font-size: 14px;
    }
  }
`;

const Accent = styled(BaseButton)`
  align-items: center;
  background: ${colors.accent1};
  border: 1px solid ${colors.accent1};
  color: white;
  display: flex;
  justify-content: center;

  &:hover {
    ${props => props.theme && setDarkenAttrs(0.1, colors.accent1)};
  }

  &:active {
    ${props => props.theme && setDarkenAttrs(0.15, colors.accent1)};
  }
`;

Apple.displayName = "Apple";
Google.displayName = "Google";
Accent.displayName = "Accent";
BaseButton.displayName = "BaseButton";
ErrorRed.displayName = "ErrorRed";
Hollow.displayName = "Hollow";
HollowDark.displayName = "HollowDark";
Primary.displayName = "Primary";
White.displayName = "White";

const ColorThemesEnum = {
  apple: Apple,
  google: Google,
  accent: Accent,
  errorRed: ErrorRed,
  hollow: Hollow,
  hollowDark: HollowDark,
  primary: Primary,
  white: White
};

ColorThemesEnum.classicBlue = ColorThemesEnum.primary;

export { BaseButton, ColorThemesEnum };
